class ProductRecommender {
  constructor(options = {}) {
    const currencySymbol = typeof currency !== 'undefined' ? currency.symbol : '$';

    this.defaults = {
      containerSelector: '.js-go-cart-recommendations',
      itemSelector: '.js-go-cart-recommendation-item',
      productUrl: '/products/',
      limit: 10,
      recommendationType: 'best-selling',
      moneyFormat: currencySymbol + "{{amount}}",
      excludeTypes: ['Older_version']
    };

    this.settings = { ...this.defaults, ...options };
    this.itemClickHandler = this.handleItemClick.bind(this);
    this.swiper = null;
    this.init();
  }

  init() {
    this.container = document.querySelector(this.settings.containerSelector);
    if (!this.container) return;

    document.addEventListener('goCart:updated', (e) => {
      this.updateRecommendations(e.detail.cart);
    });

    this.updateRecommendations();
  }

  async updateRecommendations(cart = null) {
    try {
      if (!cart) cart = await this.fetchCart();

      const recommendations = await this.fetchRecommendations(cart);
      const cartProductIds = cart.items.map(item => item.product_id.toString());
      const filtered = this.filterRecommendations(recommendations, cartProductIds);

      this.renderRecommendations(filtered);
    } catch (error) {
      console.error('推荐产品加载失败:', error);
      this.container.style.display = 'none';
    }
  }

  async fetchCart() {
    const response = await fetch('/cart.js');
    if (!response.ok) throw new Error('Failed to fetch cart');
    return await response.json();
  }

  async fetchRecommendations() {
    try {
      const response = await fetch('/collections/recommend-product/products.json?limit=10');
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      return data.products || [];
    } catch (error) {
      console.error('获取推荐产品失败:', error);
      return [];
    }
  }

  filterRecommendations(products, cartProductIds) {
    if (!products || !Array.isArray(products)) return [];

    return products
      .filter(product =>
        product.id &&
        !cartProductIds.includes(product.id.toString()) &&
        !this.settings.excludeTypes.includes(product.product_type)
      )
      .slice(0, this.settings.limit);
  }

  renderRecommendations(products) {
    if (!products.length) {
      this.container.style.display = 'none';
      return;
    }

    this.container.style.display = 'block';

    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    this.container.innerHTML = `
      <div class="jnc-rec-swiper-wrap">
        <div class="swiper jnc-rec-swiper">
          <div class="swiper-wrapper jnc-rec-wrapper">
            ${products.map(product => this.renderProduct(product)).join('')}
          </div>
        </div>
        <div class="jnc-rec-pagination"></div>
      </div>
    `;

    this.container.querySelectorAll(this.settings.itemSelector).forEach(item => {
      item.removeEventListener('click', this.itemClickHandler);
      item.addEventListener('click', this.itemClickHandler);
    });

    const swiperEl = this.container.querySelector('.jnc-rec-swiper');
    const paginationEl = this.container.querySelector('.jnc-rec-pagination');

    if (!swiperEl || typeof Swiper === 'undefined') return;

    this.swiper = new Swiper(swiperEl, {
      slidesPerView: 1,
      spaceBetween: 32, 
      loop: products.length > 1,
      speed: 850,
      grabCursor: true,
      allowTouchMove: true,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      resistanceRatio: 0.85,
      longSwipesRatio: 0.2,
      longSwipesMs: 250,
      autoplay: products.length > 1 ? {
        delay: 3200,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      pagination: products.length > 1 ? {
        el: paginationEl,
        clickable: true,
        bulletClass: 'jnc-rec-bullet',
        bulletActiveClass: 'is-active',
        renderBullet: function(index, className) {
          return `<span class="${className}" aria-label="Go to slide ${index + 1}"></span>`;
        }
      } : false,
      on: {
        init: function () {
          if (products.length <= 1 && paginationEl) {
            paginationEl.style.display = 'none';
          }
        }
      }
    });
  }

  renderProduct(product) {
    const variant = product.variants && product.variants[0];
    if (!variant) return '';

    const imageUrl = product.images?.[0]?.src || '';
    const priceCents = Math.round(parseFloat(variant.price || '0') * 100);
    const compareAtCents = variant.compare_at_price
      ? Math.round(parseFloat(variant.compare_at_price) * 100)
      : null;

    const formatMoney = window.formatMoney || ((cents, format) => {
      const amount = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', amount);
    });

    const price = formatMoney(priceCents, this.settings.moneyFormat);
    const compareAtPrice = compareAtCents
      ? `<span class="jnc-rec-compare-price">${formatMoney(compareAtCents, this.settings.moneyFormat)}</span>`
      : '';

    return `
      <div class="swiper-slide jnc-rec-item">
        <div class="jnc-rec-row">
          <a href="${this.settings.productUrl}${product.handle}" class="jnc-rec-image-wrap">
            <img src="${imageUrl}" alt="${product.title}" class="jnc-rec-image" />
          </a>

          <div class="jnc-rec-center">
            <div class="jnc-rec-title">${product.title}</div>
            <div class="jnc-rec-price">
              <span class="jnc-rec-price-current">${price}</span>
              ${compareAtPrice}
            </div>
          </div>

          <button
            class="btn js-go-cart-recommendation-item jnc-rec-btn"
            type="button"
            data-id="${variant.id}"
            data-name="${product.title}"
            onclick="dataLayer.push({
              event: 'recommendation_add_to_cart',
              product_id: this.dataset.id,
              product_name: this.dataset.name
            })"
          >
            <span
              class="jnc-rec-btn-text btn-text"
              data-id="${variant.id}"
              data-name="${product.title}"
              data-price="${variant.price}"
            >
              ADD
            </span>

            <span class="jnc-rec-btn-spinner btn-spinner" aria-hidden="true" style="display:none;">
              <svg class="jnc-rec-spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" width="18" height="18">
                <circle class="jnc-rec-spinner-path" fill="none" stroke="#ffffff" stroke-width="6" cx="33" cy="33" r="30" />
              </svg>
            </span>
          </button>
        </div>
      </div>
    `;
  }

  handleItemClick(e) {
    e.preventDefault();
    const variantId = e.currentTarget.dataset.id;
    if (variantId) {
      this.addToCart(variantId);
    }
  }

  async addToCart(variantId) {
    const button = this.container.querySelector(`.js-go-cart-recommendation-item[data-id="${variantId}"]`);
    if (!button) return;

    const spinner = button.querySelector('.btn-spinner');
    const text = button.querySelector('.btn-text');

    button.disabled = true;
    if (text) text.style.display = 'none';
    if (spinner) spinner.style.display = 'flex';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });

      if (!response.ok) throw new Error('failed add to cart');

      if (window.goCart?.fetchCart) {
        const updatedCart = await window.goCart.fetchCart();
        document.dispatchEvent(new CustomEvent('goCart:updated', {
          detail: { cart: updatedCart }
        }));
      }

      if (window.jQuery) {
        jQuery('.footer-cart-drawer').css('display', 'block');
        jQuery('.cart-title').text('Your Cart');
      } else {
        const footerDrawer = document.querySelector('.footer-cart-drawer');
        const cartTitle = document.querySelector('.cart-title');
        if (footerDrawer) footerDrawer.style.display = 'block';
        if (cartTitle) cartTitle.textContent = 'Your Cart';
      }
    } catch (error) {
      console.error('添加产品失败:', error);
    } finally {
      button.disabled = false;
      if (text) text.style.display = 'inline-block';
      if (spinner) spinner.style.display = 'none';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ProductRecommender({
    containerSelector: '.cart-recommendations-top',
    recommendationType: 'best-selling',
    productUrl: '/products/'
  });
});

document.addEventListener('click', function(event) {
  const target = event.target.closest('.btn-text');

  if (target) {
    const productName = target.getAttribute('data-name');
    const productId = target.getAttribute('data-id');
    const productPrice = target.getAttribute('data-price');

    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['track', 'Added to Cart', {
      ProductName: productName,
      ProductID: productId,
      Price: productPrice
    }]);
  }
});